'use client';

import { useState, useEffect, useCallback } from 'react';
import { useCustomerAuth } from '@/components/auth/CustomerAuthProvider';
import type { Deal } from '@/types/customer';
import { PortalLayout } from '../PortalLayout';
import { Card } from '../ui/Card';
import { Badge } from '../ui/Badge';

const STAGE_COLOURS: Record<string, string> = {
  NEGOTIATION: 'bg-amber-100 text-amber-700',
  DUE_DILIGENCE: 'bg-blue-100 text-blue-700',
  CLOSING: 'bg-purple-100 text-purple-700',
  COMPLETED: 'bg-green-100 text-green-700',
  CANCELLED: 'bg-red-100 text-red-700',
};

export function MyDeals() {
  const { getCustomerClient, isAuthenticated } = useCustomerAuth();
  const [deals, setDeals] = useState<Deal[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedDeal, setSelectedDeal] = useState<Deal | null>(null);

  const fetchDeals = useCallback(async () => {
    const client = getCustomerClient();
    if (!client) return;
    try {
      setLoading(true);
      const data = await client.getDeals();
      setDeals(Array.isArray(data) ? data : []);
    } catch (err) {
      console.warn('[MyDeals] Failed to fetch deals:', err);
    } finally {
      setLoading(false);
    }
  }, [getCustomerClient]);

  useEffect(() => { if (isAuthenticated) fetchDeals(); }, [isAuthenticated, fetchDeals]);

  const handleViewDeal = async (deal: Deal) => {
    const client = getCustomerClient();
    if (!client) return;
    try {
      const detail = await client.getDeal(deal.id);
      setSelectedDeal(detail);
    } catch (err) {
      console.warn('[MyDeals] Failed to load deal:', err);
    }
  };

  const formatCurrency = (value: number, currency: string) => {
    try {
      return new Intl.NumberFormat('en-GB', { style: 'currency', currency, maximumFractionDigits: 0 }).format(value);
    } catch {
      return `${currency} ${value.toLocaleString()}`;
    }
  };

  return (
    <PortalLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Deals</h1>
          <p className="text-gray-600 mt-1">Track your active deals and transactions</p>
        </div>

        {selectedDeal ? (
          <div className="space-y-4">
            <button onClick={() => setSelectedDeal(null)} className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
              Back to deals
            </button>
            <Card className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-gray-900">{selectedDeal.title}</h2>
                {selectedDeal.stage && <Badge className={STAGE_COLOURS[selectedDeal.stage] || 'bg-gray-100 text-gray-700'}>{selectedDeal.stage}</Badge>}
              </div>
              {selectedDeal.value != null && selectedDeal.currency && (
                <p className="text-2xl font-bold text-gray-900 mb-6">{formatCurrency(selectedDeal.value, selectedDeal.currency)}</p>
              )}
              {selectedDeal.participants && selectedDeal.participants.length > 0 && (
                <div className="mb-6">
                  <h3 className="font-semibold text-gray-900 mb-3">Participants</h3>
                  <div className="space-y-2">
                    {selectedDeal.participants.map(p => (
                      <div key={p.id} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                        <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-700 font-semibold text-sm">
                          {p.name.charAt(0)}
                        </div>
                        <div>
                          <p className="font-medium text-gray-900 text-sm">{p.name}</p>
                          <p className="text-xs text-gray-500">{p.role}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {selectedDeal.assets && selectedDeal.assets.length > 0 && (
                <div>
                  <h3 className="font-semibold text-gray-900 mb-3">Linked Properties</h3>
                  <div className="grid gap-3 sm:grid-cols-2">
                    {selectedDeal.assets.map(a => (
                      <div key={a.id} className="p-3 bg-gray-50 rounded-lg">
                        <p className="font-medium text-gray-900 text-sm">{a.name}</p>
                        {a.price != null && a.currency && (
                          <p className="text-sm text-gray-600">{formatCurrency(a.price, a.currency)}</p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </Card>
          </div>
        ) : loading ? (
          <div className="space-y-4">
            {[1, 2].map(i => <div key={i} className="h-24 bg-gray-100 rounded-xl animate-pulse" />)}
          </div>
        ) : deals.length === 0 ? (
          <Card className="p-12 text-center">
            <svg className="w-12 h-12 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M2.25 18.75a60.07 60.07 0 0115.797 2.101c.727.198 1.453-.342 1.453-1.096V18.75M3.75 4.5v.75A.75.75 0 013 6h-.75m0 0v-.375c0-.621.504-1.125 1.125-1.125H20.25M2.25 6v9m18-10.5v.75c0 .414.336.75.75.75h.75m-1.5-1.5h.375c.621 0 1.125.504 1.125 1.125v9.75c0 .621-.504 1.125-1.125 1.125h-.375m1.5-1.5H21a.75.75 0 00-.75.75v.75m0 0H3.75m0 0h-.375a1.125 1.125 0 01-1.125-1.125V15m1.5 1.5v-.75A.75.75 0 003 15h-.75M15 10.5a3 3 0 11-6 0 3 3 0 016 0zm3 0h.008v.008H18V10.5zm-12 0h.008v.008H6V10.5z" /></svg>
            <h3 className="text-lg font-semibold text-gray-900 mb-1">No active deals</h3>
            <p className="text-gray-500 text-sm">Your deals and transactions will appear here</p>
          </Card>
        ) : (
          <div className="space-y-3">
            {deals.map(deal => (
              <Card key={deal.id} className="p-5 hover:shadow-md transition-shadow cursor-pointer" onClick={() => handleViewDeal(deal)}>
                <div className="flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-semibold text-gray-900 truncate">{deal.title}</h3>
                      {deal.stage && <Badge className={STAGE_COLOURS[deal.stage] || 'bg-gray-100 text-gray-700'}>{deal.stage}</Badge>}
                    </div>
                    {deal.value != null && deal.currency && (
                      <p className="text-sm font-medium text-gray-700">{formatCurrency(deal.value, deal.currency)}</p>
                    )}
                    <p className="text-xs text-gray-400 mt-1">{new Date(deal.updatedAt).toLocaleDateString('en-GB', { year: 'numeric', month: 'short', day: 'numeric' })}</p>
                  </div>
                  <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </PortalLayout>
  );
}
